import React, { Dispatch, useEffect, useMemo, useState } from 'react'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { FormikStepper } from '../../../components/form/wizzard/FormikStepper'
import {
  useCreateAdgroupMutation,
  AdGroupPartsFragment,
  usePriorityListLazyQuery,
  useAdvertiserConfigQuery,
} from '../../../__generated__/graphql'
import { FormikValues } from 'formik'
import { useNavigation } from '../../../utils/hooks/useNavigation'
import { useCompanyDetailsContext } from '../../../utils/hooks/useCompanyDetailsContext'
import AdgroupDetailStep, { detailsSchema } from './AdgroupDetailStep'
import AdgroupBudgetStep from './AdgroupBudgetStep'
import { useCampaignIdFinder } from '../../../utils/hooks/campaign/useCampaignIdFinder'
import { Edit } from '@mui/icons-material'
import { buildAdgroupCreateFormInitialValues, buildBaseAdgroupCreateInput } from './buildAdgroupCreateInitialValues'
import { toAdGroupCreateInput } from './toAdGroupCreateInput'
import Loader from '../../../components/loader/Loader'
import { isNil } from 'lodash-es'
import { budgetAndDateSchema } from '../schema'
import { useUserPermissions } from '../../company/userPermissions'
import { useFormDialogContext } from '../../../components/layout/FormDialog'
import { useSidebarContext } from '../../../utils/providers/sidebarContext'

const CreateAdgroup: React.FC<{
  setOpen: Dispatch<React.SetStateAction<boolean>>
  campaignId?: string | number
}> = ({ setOpen, campaignId }) => {
  const [, setHasChanged] = useFormDialogContext()
  const { adgroupPriority, adgroupDatesRequired } = useUserPermissions()
  const {
    company: { id: companyId, slug },
  } = useCompanyDetailsContext()
  const { data: advertiserConfig, loading: advertiserConfigLoading } = useAdvertiserConfigQuery({
    variables: { id: companyId },
  })
  const t = useAppTranslations()
  const [error, setError] = useState(undefined)
  const { reloadItems } = useSidebarContext()
  const [createAdgroup] = useCreateAdgroupMutation()
  const [createdAdgroup, setCreatedAdgroup] = useState<AdGroupPartsFragment | null>()
  const [getPriorityList, { data: maybePriorityList, loading: priorityListLoader, called: priorityListCalled }] =
    usePriorityListLazyQuery()

  useEffect(() => {
    if (!isNil(campaignId) && adgroupPriority)
      getPriorityList({
        variables: { campaignId: campaignId },
      })
  }, [campaignId, adgroupPriority])

  const navigation = useNavigation()

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    const toCreate = toAdGroupCreateInput(values, values.campaignId)

    await createAdgroup({
      variables: {
        adgroup: toCreate,
      },
    })
      .then((res) => {
        const createdAdgroup = res?.data?.createAdGroup
        if (createdAdgroup) {
          reloadItems()
          setCreatedAdgroup(createdAdgroup)
        }
      })
      .catch((err: React.SetStateAction<undefined>) => setError(err))
  }

  const priorityList = maybePriorityList?.campaign?.advertiser?.reseller?.priorities ?? []
  const advertiserConfigData = advertiserConfig?.advertiser?.config

  const baseAdgroup = useMemo(
    () => (advertiserConfigData ? buildBaseAdgroupCreateInput(campaignId, advertiserConfigData, priorityList) : null),
    [campaignId, priorityList, advertiserConfigData]
  )

  const initialValues = useMemo(
    () => (baseAdgroup ? buildAdgroupCreateFormInitialValues(baseAdgroup) : null),
    [baseAdgroup]
  )

  if (adgroupPriority && (!priorityListCalled || priorityListLoader)) return <Loader />

  if (advertiserConfigLoading || !advertiserConfigData || !baseAdgroup || !initialValues) return <Loader />

  const successButton = {
    label: t('adgroup.goToAdgroup', { adgroupName: createdAdgroup?.name }),
    startIcon: <Edit />,
    onClick: () => {
      if (createdAdgroup) {
        navigation.goToAdgroup(slug, String(createdAdgroup.id))
        setOpen(false)
      }
    },
  }

  return (
    <FormikStepper
      initialValues={initialValues}
      onSubmit={handleSubmit}
      error={error}
      setError={setError}
      enableReinitialize={true}
      successMessage={t('adgroup.created')}
      successActionButton={successButton}
      setOpen={setOpen}
      setHasChanged={setHasChanged}
      mergeSteps
    >
      <AdgroupDetailStep
        validationSchema={detailsSchema}
        label={t('adgroup.details.title')}
        adgroup={baseAdgroup}
        priorityList={priorityList}
      />
      <AdgroupBudgetStep
        validationSchema={budgetAndDateSchema(advertiserConfigData.adGroup, { adgroupDatesRequired })}
        label={t('adgroup.budgetAndDates.title')}
        adgroup={baseAdgroup}
      />
    </FormikStepper>
  )
}

export const CreateAdGroupInCampaign: React.FC<{
  setOpen: Dispatch<React.SetStateAction<boolean>>
  campaignId?: string | number
}> = ({ setOpen, campaignId: campaignIdProp }) => {
  const campaignIdFromUrl = useCampaignIdFinder()
  const campaignId = campaignIdProp ?? campaignIdFromUrl

  if (!campaignId) return <Loader />
  return <CreateAdgroup setOpen={setOpen} campaignId={campaignId} />
}

export default CreateAdgroup
